import React, { useEffect, useRef, useState } from 'react';
import { Linkedin, Github } from 'lucide-react';
import { teamData } from '../data/mock';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';

const Team = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const currentRef = sectionRef.current;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, []);

  return (
    <section id="team" ref={sectionRef} className="py-14 md:py-20 bg-gradient-to-b from-gray-50 to-white relative overflow-hidden">
      {/* Background Decoration */}
      <div className="absolute inset-0">
        <div className="absolute top-20 left-10 w-72 h-72 bg-blue-100/30 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-cyan-100/30 rounded-full blur-3xl"></div>
      </div>

      <div className="container mx-auto px-5 sm:px-8 relative z-10">
        {/* Header */}
        <div
          className={`text-center mb-16 transform transition-all duration-1000 ${
            isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
          }`}
        >
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4 md:mb-6">
            <span className="text-gray-900">Nuestro Equipo de </span>
            <span className="bg-gradient-to-r from-blue-600 to-cyan-500 bg-clip-text text-transparent">Expertos</span>
          </h2>
          <div className="w-20 md:w-24 h-1 bg-gradient-to-r from-blue-600 to-cyan-500 mx-auto mb-6 md:mb-8"></div>
          <p className="text-base sm:text-lg md:text-xl text-gray-600 max-w-3xl mx-auto">
            Conoce a los profesionales que hacen posible la innovación tecnológica en <strong>BEZALEL TECHNOLOGY</strong>
          </p>
        </div>

        {/* Team Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-8 max-w-6xl mx-auto">
          {teamData.map((member, index) => (
            <div
              key={member.id}
              className={`transform transition-all duration-700 ${
                isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
              }`}
              style={{ transitionDelay: `${index * 150}ms` }}
            >
              <Card className="border-0 shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden group bg-white">
                <div className="relative">
                  {/* Image Container */}
                  <div className="aspect-square overflow-hidden bg-gradient-to-br from-blue-100 to-cyan-100">
                    <img
                      src={member.image}
                      alt={member.name}
                      className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-500"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.parentElement.innerHTML = `
                          <div class="w-full h-full flex items-center justify-center bg-gradient-to-br from-blue-500 to-cyan-400">
                            <span class="text-white text-6xl font-bold">${member.name.charAt(0)}</span>
                          </div>
                        `;
                      }}
                    />
                  </div>

                  {/* Overlay on Hover */}
                  <div className="absolute inset-0 bg-gradient-to-t from-blue-900/90 via-blue-900/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-6">
                    <div className="flex space-x-3">
                      {member.linkedin && member.linkedin !== '#' && (
                        <a
                          href={member.linkedin}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="w-10 h-10 bg-white rounded-full flex items-center justify-center hover:bg-blue-50 transition-colors"
                        >
                          <Linkedin className="w-5 h-5 text-blue-600" />
                        </a>
                      )}
                      {member.github && member.github !== '#' && (
                        <a
                          href={member.github}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="w-10 h-10 bg-white rounded-full flex items-center justify-center hover:bg-gray-50 transition-colors"
                        >
                          <Github className="w-5 h-5 text-gray-900" />
                        </a>
                      )}
                    </div>
                  </div>
                </div>

                <CardContent className="p-6">
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">
                    {member.name}
                  </h3>
                  <div className="inline-block px-3 py-1 bg-gradient-to-r from-blue-100 to-cyan-100 rounded-full mb-4">
                    <p className="text-sm font-semibold text-blue-700">
                      {member.role}
                    </p>
                  </div>
                  <p className="text-gray-600 leading-relaxed">
                    {member.description}
                  </p>
                </CardContent>
              </Card>
            </div>
          ))}
        </div>

        {/* CTA Section */}
        <div
          className={`mt-16 text-center transform transition-all duration-1000 delay-500 ${
            isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
          }`}
        >
          <div className="inline-block bg-gradient-to-r from-blue-600 to-cyan-500 rounded-xl md:rounded-2xl p-6 sm:p-8 shadow-xl w-full max-w-xl mx-auto">
            <h3 className="text-2xl font-bold text-white mb-4">
              ¿Listo para transformar tu negocio?
            </h3>
            <p className="text-white/90 mb-6 max-w-xl">
              Nuestro equipo está preparado para llevar tu empresa al siguiente nivel tecnológico
            </p>
            <Button
              size="lg"
              onClick={() => {
                const element = document.querySelector('#contacto');
                if (element) {
                  element.scrollIntoView({ behavior: 'smooth' });
                }
              }}
              className="bg-white text-blue-600 hover:bg-gray-100 font-semibold"
            >
              Contactar al Equipo
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Team;
